import React, { useCallback, useEffect, useState } from "react";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { localCalendarDateYYYYMMDD, spotActivityDefaultLocalRange } from "./orderFilterHelpers";
import {
  downloadUtf8Csv,
  hideExportModal,
  showExportModal,
} from "./orderExportHelpers";
import {
  EXPORT_KIND_SPOT_TRADES,
  fetchExportItems,
  getExportCellText,
  getExportColumnSpec,
  getExportCsvBasename,
  getExportTitle,
  rowsToCsv,
} from "./ordersHistoryExport";

function escapeHtmlText(val) {
  if (val === null || val === undefined) return "";
  const str = typeof val === "object" ? "" : String(val);
  if (!str || str === "[object Object]") return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function openPrintView(exportKind, items, rangeFrom, rangeTo, printWindow = null) {
  const spec = getExportColumnSpec(exportKind);
  const w = printWindow ?? window.open("about:blank", "_blank");
  if (!w) return false;

  const th = spec.map((c) => `<th>${escapeHtmlText(c.label)}</th>`).join("");
  const trs = items
    .map((row) => {
      const cells = spec
        .map((col) => `<td>${escapeHtmlText(getExportCellText(exportKind, col, row))}</td>`)
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  const heading = escapeHtmlText(getExportTitle(exportKind));
  const escPeriod = `${escapeHtmlText(rangeFrom)} – ${escapeHtmlText(rangeTo)}`;
  w.document.open();
  w.document.write(`<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${heading}</title>`);
  w.document.write(
    "<style>body{font-family:system-ui,-apple-system,sans-serif;padding:16px;margin:0;}table{border-collapse:collapse;width:100%;font-size:11px;}th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;vertical-align:top;}th{background:#eee;position:sticky;top:0;}</style>",
  );
  w.document.write(`</head><body><h1 style="font-size:16px;font-weight:600;">${heading}</h1>`);
  w.document.write(
    `<p style="margin:8px 0;">${escPeriod}</p><table><thead><tr>${th}</tr></thead><tbody>${trs}</tbody></table></body></html>`,
  );
  w.document.close();
  window.setTimeout(() => {
    try {
      w.focus();
      w.print();
    } catch {
      /* ignore */
    }
  }, 400);
  return true;
}

function presetSpanDays(daysBack) {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - Math.max(0, daysBack - 1));
  return {
    from: localCalendarDateYYYYMMDD(from),
    to: localCalendarDateYYYYMMDD(to),
  };
}

/**
 * Export modal for order / trade history pages.
 * `exportKind`: spot-trades | futures-trades | futures-positions
 */
export default function SpotOrdersExportModal({
  modalId,
  title,
  exportKind = EXPORT_KIND_SPOT_TRADES,
  defaultFrom,
  defaultTo,
  defaultPreset = "custom",
}) {
  const init = spotActivityDefaultLocalRange(30);

  const [preset, setPreset] = useState(defaultPreset || "custom");
  const [from, setFrom] = useState(defaultFrom || init.from);
  const [to, setTo] = useState(defaultTo || init.to);
  const [format, setFormat] = useState("pdf");

  const resolvedTitle = title || getExportTitle(exportKind);

  const applyPresetKey = useCallback((key) => {
    setPreset(key);
    if (key === "24h") {
      const span = presetSpanDays(1);
      setFrom(span.from);
      setTo(span.to);
      return;
    }
    if (key === "2w") {
      const span = presetSpanDays(14);
      setFrom(span.from);
      setTo(span.to);
      return;
    }
    if (key === "1m") {
      const span = presetSpanDays(30);
      setFrom(span.from);
      setTo(span.to);
      return;
    }
    if (key === "3m") {
      const span = presetSpanDays(90);
      setFrom(span.from);
      setTo(span.to);
      return;
    }
    if (key === "6m") {
      const span = presetSpanDays(180);
      setFrom(span.from);
      setTo(span.to);
      return;
    }
    setPreset("custom");
  }, []);

  const syncFromToolbar = useCallback(() => {
    setFrom(defaultFrom || init.from);
    setTo(defaultTo || init.to);
    applyPresetKey(defaultPreset || "custom");
  }, [defaultFrom, defaultTo, defaultPreset, init.from, init.to, applyPresetKey]);

  useEffect(() => {
    const el = document.getElementById(modalId);
    if (!el) return undefined;
    const onShown = () => syncFromToolbar();
    el.addEventListener("shown.bs.modal", onShown);
    return () => el.removeEventListener("shown.bs.modal", onShown);
  }, [modalId, syncFromToolbar]);

  const runSingleExport = async (rangeFrom, rangeTo, pdfParkingWindow) => {
    const result = await fetchExportItems(exportKind, rangeFrom, rangeTo);
    if (!result.ok) {
      return { ok: false, message: result.message || "Export failed." };
    }
    if (result.downloadUrl) {
      pdfParkingWindow?.close();
      window.open(result.downloadUrl, "_blank", "noopener,noreferrer");
      return { ok: true, message: result.message || "Download started.", count: 0 };
    }

    const items = result.items || [];
    if (!items.length) {
      return { ok: true, empty: true, message: "No records in the selected period." };
    }

    const rowLabel = typeof result.meta?.row_count === "number" ? result.meta.row_count : items.length;

    if (format === "excel") {
      pdfParkingWindow?.close();
      const csvBody = rowsToCsv(exportKind, items);
      const base = getExportCsvBasename(exportKind).replace(/\.csv$/i, "");
      downloadUtf8Csv(`${base}_${rangeFrom}_${rangeTo}.csv`, csvBody);
      return {
        ok: true,
        message: `${rowLabel} row${rowLabel !== 1 ? "s" : ""} exported (CSV).`,
        count: rowLabel,
      };
    }

    const opened = openPrintView(exportKind, items, rangeFrom, rangeTo, pdfParkingWindow ?? undefined);
    if (!opened) {
      return {
        ok: false,
        message: "Pop-ups are blocked — allow them for this site, or choose Excel export to download CSV.",
      };
    }
    return {
      ok: true,
      message: `${rowLabel} record${rowLabel !== 1 ? "s" : ""} prepared for print — use Save as PDF in the print dialog.`,
      count: rowLabel,
    };
  };

  const handleExport = async () => {
    if (!from || !to) {
      alertErrorMessage("Please choose a valid date range.");
      return;
    }
    if (from > to) {
      alertErrorMessage("Start date cannot be after end date.");
      return;
    }

    const pdfParkingWindow = format === "pdf" ? window.open("about:blank", "_blank") : null;

    LoaderHelper.loaderStatus(true);
    try {
      const result = await runSingleExport(from, to, pdfParkingWindow);
      if (!result.ok) {
        pdfParkingWindow?.close();
        alertErrorMessage(result.message);
        return;
      }
      if (result.empty) {
        pdfParkingWindow?.close();
        alertSuccessMessage(result.message);
        return;
      }
      alertSuccessMessage(result.message);
    } catch (e) {
      pdfParkingWindow?.close();
      alertErrorMessage(e?.message || "Export failed.");
    } finally {
      LoaderHelper.loaderStatus(false);
      window.setTimeout(() => hideExportModal(modalId), 350);
    }
  };

  return (
    <div className="modal fade search_form export_modal" id={modalId} tabIndex="-1" aria-labelledby={`${modalId}Label`} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id={`${modalId}Label`}>
              {resolvedTitle}
            </h5>
            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" />
          </div>
          <div className="modal-body export_modal_body">
            <div className="export_section">
              <label className="export_label">Select Time Period</label>
              <div className="export_btn_group export_presets">
                <button type="button" className={`export_btn${preset === "24h" ? " active" : ""}`} onClick={() => applyPresetKey("24h")}>
                  Last 24 hours
                </button>
                <button type="button" className={`export_btn${preset === "2w" ? " active" : ""}`} onClick={() => applyPresetKey("2w")}>
                  2 Weeks
                </button>
                <button type="button" className={`export_btn${preset === "1m" ? " active" : ""}`} onClick={() => applyPresetKey("1m")}>
                  1 Month
                </button>
                <button type="button" className={`export_btn${preset === "3m" ? " active" : ""}`} onClick={() => applyPresetKey("3m")}>
                  3 Months
                </button>
                <button type="button" className={`export_btn${preset === "6m" ? " active" : ""}`} onClick={() => applyPresetKey("6m")}>
                  6 Months
                </button>
                <button type="button" className={`export_btn${preset === "custom" ? " active" : ""}`} onClick={() => applyPresetKey("custom")}>
                  Customize
                </button>
              </div>
              <div className="export_date_range">
                <input
                  type="date"
                  className="export_date_input"
                  value={from}
                  onChange={(e) => {
                    setPreset("custom");
                    setFrom(e.target.value);
                  }}
                />
                <span className="export_date_arrow">
                  <i className="ri-arrow-right-line" />
                </span>
                <input
                  type="date"
                  className="export_date_input"
                  value={to}
                  onChange={(e) => {
                    setPreset("custom");
                    setTo(e.target.value);
                  }}
                />
              </div>
              <small className="export_note">* All records in the selected period will be downloaded.</small>
            </div>

            <div className="export_section">
              <label className="export_label">Format</label>
              <div className="export_btn_group">
                <button
                  type="button"
                  className={`export_btn${format === "pdf" ? " active" : ""}`}
                  onClick={() => setFormat("pdf")}
                >
                  PDF
                </button>
                <button
                  type="button"
                  className={`export_btn${format === "excel" ? " active" : ""}`}
                  onClick={() => setFormat("excel")}
                >
                  Excel
                </button>
              </div>
              <small className="export_note mt-2 d-block">
                Excel downloads CSV. PDF opens print view — choose &quot;Save as PDF&quot; in the dialog.
              </small>
            </div>

            <div className="export_section export_actions">
              <button type="button" className="export_submit_btn" onClick={() => void handleExport()}>
                Export
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { showExportModal, hideExportModal };
